import React from 'react';
import moment from 'moment-timezone';
import FinBadge from '../../connect/finance/fin-badge';
import { PlatformTypes } from '../../../data/enums';

export const DASH = '—';

export const dash = () => <span className="text-fin-faintest">{DASH}</span>;

// One date, one line ("Sep 16") — the mock's cell (spec 2026-09-21 §2),
// replacing the two-line day-over-year of 2026-09-17 that copied finance-v2.
// Shared by Order Reports and the Overview previews rather than written
// twice: a date that reads one way on one screen and another on the next is
// the kind of difference a reader notices and cannot explain.
export const dateCell = (value) => {
  if (!value) return dash();
  return (
    <span className="tabular-nums text-fin-muted-2">{moment.utc(value).local().format('MMM D')}</span>
  );
};

// Stripe's own dates — funds-available, payout arrival — are days, not
// instants. Rendering them through the local clock can shift them across
// midnight and show a restaurant the wrong day, so these read in UTC exactly
// as Stripe stated them.
export const utcDateCell = (value) => {
  if (!value) return dash();
  return <span className="tabular-nums text-fin-muted-2">{moment.utc(value).format('MMM D')}</span>;
};

// The status a restaurant is asking about on a finance screen is not where
// the order is in the kitchen — it is whether the money has landed.
//
// Compared as instants, not as days. `fundsavailableon` is Stripe's
// balance-transaction `available_on`, a unix timestamp, so a payment
// released at 08:00 UTC must not be called Available from 00:00 UTC — up to
// a day early, and early is the one direction a claim about money must
// never be wrong in.
//
// No date is not "pending": it is not known yet. Saying "Pending" there
// would assert something Stripe has not told us.
// Owner, 2026-09-25 (supersedes the refund-first rule of 2026-09-23): the
// status says whether the money is Available or Pending, whatever was
// refunded — the Refund column carries the refund. Same words as the
// export's Status column.
export const availabilityCell = (fundsAvailableOn) => {
  if (!fundsAvailableOn) return dash();
  const available = moment.utc().isSameOrAfter(moment.utc(fundsAvailableOn));
  return <FinBadge tone={available ? 'available' : 'pending'} />;
};

// Where the order was placed (owner, 2026-09-25), in the words the order
// screens already use (PlatformTypes). A code we do not know is a dash, not
// a guess.
export const platformCell = (code) => {
  const label = code === null || code === undefined ? null : PlatformTypes[code];
  return label ? <span className="text-fin-muted-2">{label}</span> : dash();
};

// An instant, in the store's own time (owner, 2026-09-25): the date, and the
// time on a line under it. A store without a timezone — or with one
// moment-timezone does not know — reads in the browser's zone rather than
// not at all.
export const zonedDateTimeCell = (value, timezoneId) => {
  if (!value) return dash();
  const instant = moment.utc(value);
  const zoned = timezoneId && moment.tz.zone(timezoneId) ? instant.tz(timezoneId) : instant.local();
  return (
    <span className="flex flex-col leading-tight tabular-nums text-fin-muted-2">
      <span>{zoned.format('MMM D')}</span>
      <span className="text-[12px] text-fin-faint">{zoned.format('h:mm A')}</span>
    </span>
  );
};
